import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Box, Button, Grid, CircularProgress } from '@material-ui/core';

import { Label } from 'core';
import pen from 'core/assets/svg/pen.svg';
import rightArrow from 'core/assets/svg/ic-arrow.svg';
import download from 'core/assets/svg/download.svg';
import useStyles from './signed-documents-required-section.styles';
import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';

const CoverageChange = ({ trailingDocChange, preview, hideDownloadButton }) => {
  const classes = useStyles();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const {
    account: {
      policies: {
        generateTemporaryDocument,
        policy: {
          policy: { id, state }
        }
      },
      email
    }
  } = useStore();

  const fetchDocument = async (docType, addedChangeKey) => {
    try {
      setSubmitting(true);
      const response = await generateTemporaryDocument(id, preview.id, docType, email, addedChangeKey);
      if (response.data.signedUrl) {
        window.open(response.data.signedUrl, '_blank');
      } else {
        toast.notify({
          type: 'error',
          message: 'The document failed to generate, try again. If the problem persists, please contact support.'
        });
      }
      setSubmitting(false);
    } catch {
      setSubmitting(false);
      toast.notify({
        type: 'error',
        message: 'The document failed to generate, try again. If the problem persists, please contact support.'
      });
    }
  };

  return (
    <Box className={classes.coverageChange}>
      <img src={pen} alt="pen" />
      <Grid className={classes.fieldAndChange}>
        <Label type="darkGreenSmall" className={classes.fieldName}>
          {trailingDocChange.addedChangeKey}
        </Label>
        {['AZ', 'AR', 'KY', 'MA', 'MO', 'WI', 'DC', 'WV'].includes(state) &&
        trailingDocChange.addedChangeKey === 'Un/Underinsured Motorist Bodily Injury' ? (
          <Grid container className={classes.changeInfo}>
            <Grid container>
              <Label fontSize={12} type="greenSmall" className={classes.change}>
                UM Change
              </Label>
              <Label fontSize={12} fontWeight="500" type="discount" className={classes.removedValue}>
                {trailingDocChange.oldValueUM}
              </Label>
              <img src={rightArrow} alt="right gray arrow" className={classes.rightArrow} />
              <Label fontSize={12} type="darkSmall" className={classes.addedValue}>
                {trailingDocChange.newValueUM}
              </Label>
            </Grid>
            <Grid container>
              <Label fontSize={12} type="greenSmall" className={classes.change}>
                UIM Change
              </Label>
              <Label fontSize={12} fontWeight="500" type="discount" className={classes.removedValue}>
                {trailingDocChange.oldValueUIM}
              </Label>
              <img src={rightArrow} alt="right gray arrow" className={classes.rightArrow} />
              <Label fontSize={12} type="darkSmall" className={classes.addedValue}>
                {trailingDocChange.newValueUIM}
              </Label>
            </Grid>
          </Grid>
        ) : (
          <Grid container className={classes.changeInfo}>
            <Label fontSize={12} type="greenSmall" className={classes.change}>
              Change
            </Label>
            <Label fontSize={12} fontWeight="500" type="discount" className={classes.removedValue}>
              {trailingDocChange.oldValue}
            </Label>
            <img src={rightArrow} alt="right gray arrow" className={classes.rightArrow} />
            <Label fontSize={12} type="darkSmall" className={classes.addedValue}>
              {trailingDocChange.newValue}
            </Label>
          </Grid>
        )}
      </Grid>
      {!hideDownloadButton && (
        <Button
          size="small"
          disableFocusRipple
          variant="text"
          color="secondary"
          onClick={() => fetchDocument(trailingDocChange.documentType, trailingDocChange.addedChangeKey)}
        >
          {submitting ? (
            <CircularProgress size={24} className={classes.loading} noBackground />
          ) : (
            <img alt="download icon" src={download} />
          )}
          Download document
        </Button>
      )}
    </Box>
  );
};

CoverageChange.propTypes = {
  trailingDocChange: PropTypes.object,
  preview: PropTypes.shape({
    coverageChange: PropTypes.string,
    itemsChanged: PropTypes.string,
    billingDescription: PropTypes.string,
    newPremium: PropTypes.number,
    newTotalPrice: PropTypes.number,
    id: PropTypes.string,
    endDate: PropTypes.string,
    nextPayments: PropTypes.array
  })
};

CoverageChange.defaultProps = {
  trailingDocChange: {}
};

export default observer(CoverageChange);
